import { ElMessageBox } from 'element-plus'
import { isWechatBrowser, WX_LOGIN_MANUAL_KEY } from '@/utils/browser'

const WECHAT_FIRST_VISIT_KEY = 'wechat_first_visit_prompted'

export function isLoggedIn() {
  const token = localStorage.getItem('jwt_token')
  const expireTime = parseInt(localStorage.getItem('expire_time'), 10)
  return !!(token && expireTime && expireTime > Date.now())
}

/** Path after origin, e.g. /knowledge/courses/display/1 */
export function normalizeReturnPath(path) {
  if (!path || typeof path !== 'string') {
    return '/'
  }
  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      const url = new URL(path)
      return url.pathname + url.search + url.hash
    } catch {
      return '/'
    }
  }
  return path.startsWith('/') ? path : `/${path}`
}

export function wechatOfficialLoginUrl(returnPath, coupon) {
  const href = normalizeReturnPath(returnPath)
  let url = `/api/v1/wechat/official/login?href=${encodeURIComponent(href)}`
  const c = typeof coupon === 'string' ? coupon.trim() : ''
  if (c) {
    url += `&coupon=${encodeURIComponent(c)}`
  }
  return url
}

export function buildWechatOfficialLoginUrl({ href, coupon } = {}) {
  const path = href ? normalizeReturnPath(href) : '/'
  return wechatOfficialLoginUrl(path, coupon)
}

export function markWechatSkipAutoLogin() {
  sessionStorage.setItem(WX_LOGIN_MANUAL_KEY, '1')
}

export function clearWechatSkipAutoLogin() {
  sessionStorage.removeItem(WX_LOGIN_MANUAL_KEY)
}

/** 用户主动退出或 OAuth 失败后，不再自动跳转微信一键登录 */
export function shouldSkipWechatAutoLogin(route) {
  if (sessionStorage.getItem(WX_LOGIN_MANUAL_KEY) === '1') {
    return true
  }
  const q = route?.query
  return q?.manual === '1' || q?.pwd === '1'
}

export function loginPageRoute(returnPath, extraQuery = {}) {
  return {
    path: '/login',
    query: { href: normalizeReturnPath(returnPath), ...extraQuery }
  }
}

export function loginPagePath(returnPath) {
  const href = normalizeReturnPath(returnPath)
  return `/login?href=${encodeURIComponent(href)}`
}

export function decodeWechatQRRedirectPath(state) {
  return normalizeReturnPath(parseWechatQRState(state).href || '/')
}

/** OAuth state: base64 JSON { href, sid, examId, coupon } */
export function parseWechatQRState(state) {
  if (!state || state === 'STATE_STRING') {
    return { href: '', sid: '', examId: '', coupon: '' }
  }
  try {
    const base64 = state.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const raw = atob(padded)
    if (raw.startsWith('{')) {
      const o = JSON.parse(raw)
      return {
        href: o.href || '',
        sid: o.sid || '',
        examId: o.examId || '',
        coupon: o.coupon || ''
      }
    }
    if (raw.startsWith('/') && !raw.startsWith('//')) {
      return { href: raw, sid: '', examId: '', coupon: '' }
    }
  } catch {
    // ignore invalid state
  }
  return { href: '', sid: '', examId: '', coupon: '' }
}

export function buildWechatQRLoginApiUrl({ origin, href, sid, examId, coupon } = {}) {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '')
  const redirectUri = `${base}/wechatcallback`
  let url = `${base}/api/v1/wechat/login?redirect_uri=${encodeURIComponent(redirectUri)}`
  const normalizedHref = href ? normalizeReturnPath(href) : ''
  if (normalizedHref && normalizedHref !== '/') {
    url += `&href=${encodeURIComponent(normalizedHref)}`
  }
  if (sid) {
    url += `&sid=${encodeURIComponent(sid)}`
  }
  if (examId) {
    url += `&examId=${encodeURIComponent(String(examId))}`
  }
  if (coupon) {
    url += `&coupon=${encodeURIComponent(coupon)}`
  }
  return url
}

export function goToLogin(router, returnPath) {
  const path = returnPath ?? router?.currentRoute?.value?.fullPath ?? '/'
  const extra = sessionStorage.getItem(WX_LOGIN_MANUAL_KEY) === '1' ? { manual: '1' } : {}
  return router.push(loginPageRoute(path, extra))
}

export function hasWechatFirstVisitPrompted() {
  return sessionStorage.getItem(WECHAT_FIRST_VISIT_KEY) === '1'
}

export function markWechatFirstVisitPrompted() {
  sessionStorage.setItem(WECHAT_FIRST_VISIT_KEY, '1')
}

/**
 * 弹窗确认后跳转登录页（全环境统一走 /login?href=...）。
 * @returns {Promise<'navigate'|false>}
 */
export async function promptLogin(message) {
  try {
    await ElMessageBox.confirm(message, '登录提示', {
      confirmButtonText: '去登录',
      cancelButtonText: '取消',
      type: 'info'
    })
    return 'navigate'
  } catch {
    return false
  }
}

/**
 * 微信内首次进入站点：询问是否登录；确认后跳转登录页。
 * @returns {Promise<'navigate'|false>}
 */
export async function promptWechatFirstVisit() {
  if (!isWechatBrowser() || isLoggedIn() || hasWechatFirstVisitPrompted()) {
    return false
  }
  if (sessionStorage.getItem(WX_LOGIN_MANUAL_KEY) === '1') {
    return false
  }

  markWechatFirstVisitPrompted()

  try {
    await ElMessageBox.confirm(
      '是否前往登录？取消后可自由浏览网站内容。',
      '登录提示',
      {
        confirmButtonText: '去登录',
        cancelButtonText: '暂不登录',
        type: 'info'
      }
    )
    return 'navigate'
  } catch {
    return false
  }
}
